/**
 * DemoChainPage — auth-free magic. The page a stranger from Kickstarter can
 * land on directly. Shows ONE locked chain in full magazine layout, sandboxed
 * (no comments, no review actions, no narration spend).
 *
 * Route: /demo
 * No login wall. No paywall. Just the methodology working in 30 seconds.
 *
 * Conversion CTA at the end: "Walk more chains" → /signup
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, ArrowRight, ArrowLeft, BookOpen } from 'lucide-react';
import api from '../lib/api';
import InspiredBy from './content/InspiredBy';
import StoneTablet from './StoneTablet';
import NarrationPlayButton from './NarrationPlayButton';
import InlineCitations from './InlineCitations';
import InlineAnchors from './InlineAnchors';
import PinholeTransition from './content/PinholeTransition';
import ParagraphFollowCursor from './ParagraphFollowCursor';
// GlobeSpinIntro removed Apr 30 2026 per Lead — DO NOT re-add. The spin
// overlay was visually buggy on /demo (re-mounting on every parent
// re-render → strobe), and Lead opted to ship the chain content directly
// without the time-machine spin transition.
import EpisodeFrame from './EpisodeFrame';
import { useNarrationAlignment, findActiveWordIdx } from '../lib/narrationAlignment';

const STEP_KEYS = ['origin', 'change_a', 'change_b', 'contradiction', 'modern_pattern'];
const STEP_LABELS = {
  origin: 'Origin',
  change_a: 'Change A',
  change_b: 'Change B',
  contradiction: 'Contradiction',
  modern_pattern: 'Modern Pattern',
};

export default function DemoChainPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [chain, setChain] = useState(null);
  const [error, setError] = useState('');
  const [pinholeActive, setPinholeActive] = useState(false);
  const [pinholeImage, setPinholeImage] = useState(null);
  // GlobeSpinIntro removed Apr 30 2026 per Lead — DO NOT re-add. The spin
  // overlay was strobing on every audio tick because its WebGL useEffect
  // depended on a fresh `onComplete` reference every parent render, so
  // it tore down + re-built the Three.js scene on every narration:tick
  // dispatch. Page now goes straight from load → chain content.
  // Apr 30 2026 — page-level audio progress + word-level alignment.
  // Lead's call: "very precisely following my voice." We hit
  // /api/narration/chain/<id>/full/alignment for word timings, then
  // resolve activeWordIdx every audio tick so each rendered word can
  // light up exactly when Midnite says it.
  const [audioFraction, setAudioFraction] = useState(0);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const walkerId = 'c929d883-96b7-426e-aa9c-e30a0b997b96';
  const { voiceText, words, wordRanges, ready: alignmentReady } = useNarrationAlignment(
    chain?.id || walkerId,
    'full',
  );
  const activeWordIdx = findActiveWordIdx(words, audioCurrentTime);

  useEffect(() => {
    const h = (e) => {
      const d = e.detail || {};
      if (d.duration) setAudioFraction(d.currentTime / d.duration);
      if (typeof d.currentTime === 'number') setAudioCurrentTime(d.currentTime);
      setAudioPlaying(!!d.playing);
    };
    window.addEventListener('narration:tick', h);
    return () => window.removeEventListener('narration:tick', h);
  }, []);

  const queryId = searchParams.get('id');

  useEffect(() => {
    let alive = true;
    setChain(null);
    setError('');
    (async () => {
      try {
        const r = await api.get('/demo/chain', { params: queryId ? { id: queryId } : {} });
        if (alive) {
          setChain(r.data);
          setPinholeImage(r.data?.pinhole_opening_url || null);
          if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'instant' });
        }
      } catch (e) {
        if (alive) setError(e.response?.data?.detail || 'Demo chain unavailable. We\'ll have one published shortly.');
      }
    })();
    return () => { alive = false; };
  }, [queryId]);

  // Spin-intro effect removed Apr 30 2026 — see comment at the top of the
  // component. The chain content renders as soon as the API call resolves.

  const handlePinholeComplete = () => {
    setPinholeActive(false);
  };

  const goToChain = (chainId) => {
    if (!chainId) return;
    setSearchParams({ id: chainId });
  };

  // Friendly redirect helper for the lineage-restricted /demo route
  const goBackToDemo = () => {
    setSearchParams({});
  };

  if (error) {
    const isLineage404 = /demo lineage/i.test(error);
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#0A0A0A' }} data-testid="demo-chain-error">
        <div className="max-w-md text-center">
          <div className="font-historical text-[#D4A84A] text-3xl font-light mb-3">
            {isLineage404 ? 'Off the demo path' : 'No demo chain published yet'}
          </div>
          <p className="font-mono text-sm text-white/65 mb-5">{error}</p>
          {isLineage404 ? (
            <button
              onClick={goBackToDemo}
              data-testid="demo-back-to-spine-btn"
              className="font-hud text-xs tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-5 py-2 hover:bg-[#D4A84A]/85"
            >
              RETURN TO DEMO
            </button>
          ) : (
            <button
              onClick={() => navigate('/welcome')}
              data-testid="demo-back-btn"
              className="font-hud text-xs tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-5 py-2 hover:bg-[#D4A84A]/85"
            >
              BACK TO LANDING
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!chain) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0A0A' }}>
        <Loader2 size={20} className="animate-spin text-[#2F8A92]" />
      </div>
    );
  }

  const era = chain.era || 'modern';

  // ─── Picture Show slot — JUST the cinematic image. Apr 29 2026 Lead's
  // call: "The writings on the picture should be below the picture not on
  // it." The era + title + description block previously overlaid here is
  // moved into the scriptSlot below the picture frame.
  const pictureSlot = (
    <>
      {chain.pinhole_opening_url ? (
        <img
          src={chain.pinhole_opening_url}
          alt={chain.title || 'chain pinhole'}
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="episode-picture-image"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a1628] via-[#1a3a5e] to-[#0a0a0a]" />
      )}
    </>
  );

  // Apr 29 2026: sidebar removed by Lead's call — page is now full-width.
  // Sources from the chain's source_registry (the methodology-essential
  // [S_BRUNET_2002]-style citations) get inlined at the bottom of the
  // scriptSlot instead of the sidebar's "Credits" pane.
  const sourceRegistryEntries = chain.source_registry
    ? Object.entries(chain.source_registry)
    : [];

  // ─── Script slot — chisel-narrated origin scene FIRST (so the picture
  // above + this stone tablet are the first beat after pinhole opens),
  // then the step ribbon + step sections + spine nav. Apr 28 2026 brief:
  // "the picture and script do not overlap" — they are stacked vertically,
  // picture above, chisel narration below, then full step content.
  const originText = (() => {
    const o = chain.origin || {};
    let raw = '';
    if (Array.isArray(o.body_v2) && o.body_v2.length > 0) {
      raw = o.body_v2.map(b => b.text || '').filter(Boolean).join(' ');
    } else {
      raw = o.script || o.content || o.title || '';
    }
    // Strip authorial markup that should not appear chiseled on stone:
    // ** bold markers, [HOST]/[NARRATOR]/etc. speaker tags, and [pause]
    // direction tokens. The narration source mixes prose + stage cues;
    // we only chisel the prose.
    return raw
      .replace(/\*\*/g, '')
      .replace(/\[(?:HOST|NARRATOR|VO|VOICEOVER|SCENE|CUE)\]/gi, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  })();
  const scriptSlot = (
    <article className="max-w-4xl mx-auto" data-testid="demo-host-narration">
      {/* Demo banner — Lead's Apr 29 2026 brief: "after the demo they should
          be able to create a profile. Make sure they understand its a demo
          on the history walked page that they cam start their profile after
          the demo." Visible at the top so judges read it before scrolling. */}
      <div
        className="mb-8 border-l-2 border-cyan-400 pl-4 py-3 rounded-r-md"
        style={{ background: 'rgba(47,138,146,0.06)' }}
        data-testid="demo-mode-banner"
      >
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className="font-mono text-[10px] tracking-[0.32em] uppercase text-cyan-300">⟨ Demo mode ⟩</span>
          <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-white/55">a sample chain — the methodology in motion</span>
        </div>
        <p className="font-historical text-white/85 text-sm md:text-base leading-relaxed">
          This is one chain out of the spine. When you finish reading, you can{' '}
          <button
            onClick={() => navigate('/signup')}
            data-testid="demo-banner-signup"
            className="text-[#D4A84A] underline decoration-dotted underline-offset-2 hover:text-white transition-colors"
          >
            create your profile
          </button>{' '}
          and start your reading log — every walked chain logs there automatically, every primary
          source you click is saved.
        </p>
      </div>

      {/* Era + Title + Description band — moved Apr 29 2026 from being
          overlaid on the picture to its proper place BELOW the picture
          on the page background. Reads cleanly with no overlay tint. */}
      <header className="mb-10">
        <div className="font-mono text-xs md:text-sm text-[#D4A84A] tracking-[0.4em] uppercase mb-3" data-testid="demo-chain-era">
          {era.replace('_', ' ')}
        </div>
        <h1 className="font-historical text-3xl md:text-5xl lg:text-6xl text-[#D4A84A] font-light tracking-tight leading-tight mb-4" data-testid="demo-chain-title">
          {chain.title || chain.subject}
        </h1>
        {chain.description && (
          <p className="chain-prose text-lg md:text-xl">{chain.description}</p>
        )}
      </header>

      {originText && (
        <div className="mb-12" data-testid="episode-stone-narration">
          {/* Voice narration auto-play — Lead's Apr 29 2026 brief: "Narration
              should begin as soon as a page starts. Complete Narration. Not
              just a section." Plays the FULL chain script (origin → modern
              pattern composed end-to-end) in Midnite's voice clone, fired
              automatically once the audio cache is confirmed available. */}
          <NarrationPlayButton chainId={chain.id} stepKey="full" autoPlay={true} />
          <StoneTablet text={originText} speedCps={26} />
        </div>
      )}

      <nav className="flex flex-wrap gap-2 mb-8" data-testid="demo-step-ribbon">
        {STEP_KEYS.map((k) => (
          chain[k] && (
            <a
              key={k}
              href={`#step-${k}`}
              className="font-hud text-[11px] md:text-xs tracking-widest text-white/65 border border-[#D4A84A]/20 rounded-full px-3 py-2 hover:text-[#D4A84A] hover:border-[#D4A84A]/50 transition-colors"
            >
              {STEP_LABELS[k] || k}
            </a>
          )
        ))}
      </nav>

      {/* Apr 30 2026 — Lead's call: "highlighting too far ahead of speaker."
          Root cause: the audio bake includes the chain `description` prose
          before the 5 step sections, but the page only renders those 5
          sections. So a linear chars→time mapping that ignored the
          description always ran ~10–15% ahead of the narrator. The IIFE
          below now strips inline citation markers + includes the
          description in the offset baseline so each step's char range
          maps to the same time range the audio is actually in. */}
      {(() => {
        const _voiceClean = (s) => (s || '')
          .replace(/\[(?:\d+|S_[A-Z0-9_]+)\]/g, '')
          .replace(/\s+/g, ' ')
          .trim();
        const stepCleans = STEP_KEYS.map((k) => {
          const s = chain[k];
          if (!s) return '';
          return _voiceClean(s.script || s.content || '');
        });
        const stepLens = stepCleans.map((s) => s.length);
        // For section-level phase (used by the wrapper section's
        // background/highlight CSS) we still need a fraction-based
        // boundary. Use the simple linear approach here — it's only
        // for whole-section fade in/out, not the precise word
        // highlighting (which uses real word timestamps).
        const SEP = 4;
        const descLen = _voiceClean(chain.description).length;
        const totalLen = stepLens.reduce(
          (a, b, i) => a + b + (b > 0 ? SEP : 0),
          descLen + (descLen > 0 ? SEP : 0),
        ) || 1;
        const offsets = [];
        let acc = descLen + (descLen > 0 ? SEP : 0);
        STEP_KEYS.forEach((k, i) => {
          offsets.push(acc);
          if (stepLens[i] > 0) acc += stepLens[i] + SEP;
        });
        // Resolve per-step voice_char_offset in the actual `voice_text`
        // returned by the alignment endpoint (may differ slightly from
        // the local _voiceClean). indexOf is O(n) but n ≤ 13K here,
        // and we only run it on chain load.
        const voiceCharOffsets = stepCleans.map((s) => {
          if (!voiceText || !s) return 0;
          const idx = voiceText.indexOf(s.slice(0, 80));
          return idx >= 0 ? idx : 0;
        });

        return STEP_KEYS.map((key, idx) => {
          const step = chain[key];
          if (!step) return null;
          const stepStart = offsets[idx] / totalLen;
          const stepEnd = (offsets[idx] + stepLens[idx]) / totalLen;
          let phase = 'upcoming';
          if (audioFraction >= stepEnd) phase = 'past';
          else if (audioFraction >= stepStart) phase = 'active';
          const sectionCls =
            phase === 'active'
              ? 'chain-step chain-step-active'
              : phase === 'past'
                ? 'chain-step chain-step-past'
                : 'chain-step chain-step-upcoming';
          return (
            <section
              key={key}
              id={`step-${key}`}
              className={`mt-12 scroll-mt-24 ${sectionCls}`}
              data-testid={`demo-step-${key}`}
              data-step-phase={phase}
            >
              <div className="font-mono text-xs md:text-sm tracking-[0.4em] uppercase text-[#2F8A92]/95 mb-2">
                {STEP_LABELS[key] || key.replace('_', ' ')}
              </div>
              <h2 className="font-historical text-2xl md:text-4xl text-[#D4A84A] font-light tracking-tight leading-tight mb-4">
                {step.title}
              </h2>
              {step.location && (
                <div className="font-mono text-[10px] md:text-xs tracking-[0.22em] uppercase text-white/55 mb-4">
                  {step.location}
                  {step.year && <> · <span className="text-[#D4A84A]/85">{step.year < 0 ? `${Math.abs(step.year).toLocaleString()} BCE` : `${step.year} CE`}</span></>}
                </div>
              )}

              {Array.isArray(step.body_v2) && step.body_v2.length > 0 ? (
                <InlineAnchors
                  body={step.body_v2}
                  sourceRegistry={chain.source_registry || {}}
                  testIdPrefix={`demo-anchors-${key}`}
                  className="chain-prose space-y-4"
                />
              ) : (
                <InlineCitations
                  body={step.script || step.content || ''}
                  sources={step.sources || []}
                  testIdPrefix={`demo-cite-${key}`}
                  voiceText={voiceText}
                  wordRanges={wordRanges}
                  voiceCharOffset={voiceCharOffsets[idx]}
                  activeWordIdx={alignmentReady ? activeWordIdx : -1}
                  isPlaying={audioPlaying}
                />
              )}

              {/* Inline citation chip — when the canonical_spine.py prose
                  attaches a `source` id, render a Courier `<a class='cite'>`
                  pointing to the corresponding source_registry entry so
                  judges click straight to the peer-reviewed paper. */}
              {step.source && chain.source_registry?.[step.source]?.url && (
                <div className="mt-3">
                  <a
                    href={chain.source_registry[step.source].url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cite"
                    title={chain.source_registry[step.source].label || chain.source_registry[step.source].title || step.source}
                    data-testid={`demo-step-cite-${key}`}
                  >
                    {step.source}
                  </a>
                  <span className="ml-2 font-mono text-[11px] text-white/55">
                    {chain.source_registry[step.source].label || chain.source_registry[step.source].title}
                  </span>
                </div>
              )}

              {step.weird_fact && (
                <div className="mt-5 border-l-2 border-[#D4A84A]/40 pl-4">
                  <div className="font-mono text-xs tracking-widest uppercase text-[#D4A84A] mb-1.5">Did you know?</div>
                  <p className="font-mono text-sm md:text-base text-white/90 italic leading-relaxed">{step.weird_fact}</p>
                </div>
              )}
            </section>
          );
        });
      })()}

      <InspiredBy chainId={chain.id} />

      {(chain.spine?.previous || chain.spine?.next) && (
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="demo-spine-nav">
          {chain.spine?.previous ? (
            <button
              onClick={() => goToChain(chain.spine.previous.id)}
              data-testid="demo-spine-prev"
              className="group text-left border border-[#D4A84A]/20 hover:border-[#D4A84A]/55 transition-colors p-5 rounded"
            >
              <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.4em] uppercase text-white/55 mb-1.5">
                <ArrowLeft size={12} /> Previous chain
              </div>
              <div className="font-historical text-lg md:text-xl text-[#D4A84A] leading-snug group-hover:text-white transition-colors">
                {chain.spine.previous.title}
              </div>
              {chain.spine.previous.era && (
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/45 mt-1.5">
                  {String(chain.spine.previous.era).replace('_', ' ')}
                </div>
              )}
            </button>
          ) : <div />}

          {chain.spine?.next ? (
            <button
              onClick={() => goToChain(chain.spine.next.id)}
              data-testid="demo-spine-next"
              className="group text-right border border-[#D4A84A]/20 hover:border-[#D4A84A]/55 transition-colors p-5 rounded md:col-start-2"
            >
              <div className="flex items-center justify-end gap-2 font-mono text-[10px] tracking-[0.4em] uppercase text-white/55 mb-1.5">
                Next chain <ArrowRight size={12} />
              </div>
              <div className="font-historical text-lg md:text-xl text-[#D4A84A] leading-snug group-hover:text-white transition-colors">
                {chain.spine.next.title}
              </div>
              {chain.spine.next.era && (
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/45 mt-1.5">
                  {String(chain.spine.next.era).replace('_', ' ')}
                </div>
              )}
            </button>
          ) : null}
        </div>
      )}

      <div className="mt-16 border-t border-[#D4A84A]/20 pt-10 text-center" data-testid="demo-cta-block">
        {chain.spine?.next ? (
          // User is mid-spine. Primary CTA = CONTINUE YOUR JOURNEY (fires the
          // pinhole on the next chain); secondary = BACK TO TIME MACHINE.
          <>
            <h3 className="font-historical text-2xl md:text-4xl text-[#D4A84A] font-light leading-tight mb-3">
              The spine keeps walking.
            </h3>
            <p className="font-mono text-sm md:text-base text-white/75 max-w-2xl mx-auto leading-relaxed mb-7">
              Next on the chain: <span className="text-[#D4A84A]">{chain.spine.next.title}</span>
              {chain.spine.next.era && <> · <span className="text-white/55 uppercase tracking-widest text-xs">{String(chain.spine.next.era).replace('_', ' ')}</span></>}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => goToChain(chain.spine.next.id)}
                data-testid="demo-continue-journey"
                className="inline-flex items-center justify-center gap-2 font-hud text-xs md:text-sm tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-6 py-3 hover:bg-[#D4A84A]/85"
              >
                <BookOpen size={14} /> CONTINUE YOUR JOURNEY <ArrowRight size={14} />
              </button>
              <button
                onClick={() => navigate('/world')}
                data-testid="demo-return-to-time-machine"
                className="inline-flex items-center justify-center gap-2 font-hud text-xs md:text-sm tracking-widest text-white/85 border border-[#2F8A92]/40 px-6 py-3 hover:text-[#2F8A92]"
              >
                <ArrowLeft size={14} /> BACK TO WORLD HISTORY
              </button>
            </div>
          </>
        ) : (
          // End of spine — surface the paid journey CTA (existing behavior).
          <>
            <h3 className="font-historical text-2xl md:text-4xl text-[#D4A84A] font-light leading-tight mb-3">
              That was one chain.
            </h3>
            <p className="font-mono text-sm md:text-base text-white/75 max-w-2xl mx-auto leading-relaxed mb-7">
              We have hundreds more — and your account on TSOGN unlocks the whole spine, the contradictions, the cross-references that connect every era to every other.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate('/signup')}
                data-testid="demo-cta-signup"
                className="inline-flex items-center justify-center gap-2 font-hud text-xs md:text-sm tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-6 py-3 hover:bg-[#D4A84A]/85"
              >
                <BookOpen size={14} /> BEGIN YOUR JOURNEY <ArrowRight size={14} />
              </button>
              <button
                onClick={() => navigate('/world')}
                data-testid="demo-return-to-time-machine-end"
                className="inline-flex items-center justify-center gap-2 font-hud text-xs md:text-sm tracking-widest text-white/85 border border-[#2F8A92]/40 px-6 py-3 hover:text-[#2F8A92]"
              >
                <ArrowLeft size={14} /> BACK TO WORLD HISTORY
              </button>
            </div>
          </>
        )}
      </div>

      {/* ── For Teachers — Apr 30 2026 iter_65 per Lead's brief.
           Restored after a prior agent removed it. Sits below the CTA
           block, above the source registry. Top banner: "The first
           contest starts the day of our grand opening." Body: full text
           Lead supplied verbatim. ── */}
      <section className="mt-16 border-t border-[#D4A84A]/22 pt-12" data-testid="demo-for-teachers">
        <div
          className="rounded-md border border-[#D4A84A]/55 bg-[#0A0A0A] px-6 md:px-8 py-4 mb-8 inline-flex items-center gap-3"
          style={{ boxShadow: '0 0 28px rgba(212,168,74,0.12)' }}
          data-testid="demo-contest-banner"
        >
          <span className="font-hud text-[11px] md:text-sm tracking-[0.32em] uppercase text-[#D4A84A]">
            The first contest starts the day of our grand opening.
          </span>
        </div>
        <p className="font-mono text-sm md:text-base text-[#2F8A92] tracking-[0.3em] uppercase mb-3" data-testid="demo-for-teachers-eyebrow">
          For teachers
        </p>
        <h3 className="font-historical text-3xl md:text-4xl lg:text-5xl text-white font-light leading-tight mb-6" data-testid="demo-for-teachers-headline">
          Tell us what you think.
        </h3>
        <div className="font-historical text-base md:text-lg lg:text-xl text-white/85 max-w-3xl leading-relaxed space-y-4">
          <p>
            I am committed to making learning something that brings history to life. My goal is to make
            learning fun — not feel like a textbook.
          </p>
          <p>
            We all have different learning styles, and too often people get left behind. My goal is to
            take the excuses away from learning and put it all in one place — so you&rsquo;re not
            wasting valuable time hunting down sources. We want to make your life easier.
          </p>
          <p>
            My sister is a teacher. I wanted to be one too. Life took me down another path, but I never
            stopped caring about what good teaching can do for a kid.
          </p>
          <p>
            So I built this. To stop kids from falling through the cracks the way too many of us did.
          </p>
          <blockquote
            className="border-l-2 border-[#D4A84A] pl-5 my-7 italic text-[#D4A84A]"
            data-testid="demo-for-teachers-quote"
          >
            &ldquo;Everybody is a genius. But if you judge a fish by its ability to climb a tree, it
            will live its whole life believing that it is stupid.&rdquo;
            <span className="block mt-2 text-sm md:text-base font-mono not-italic text-white/55 tracking-wide">
              — Often misattributed to Albert Einstein. Source unknown.
            </span>
          </blockquote>
          <p>
            That quote is why I&rsquo;m here. Every kid learns differently. Every kid deserves a chance
            to walk history themselves — not just be told what to memorize.
          </p>
          <p>
            Take a look around. Tell me what works. Tell me what doesn&rsquo;t.
          </p>
          <p className="text-[#D4A84A] font-historical text-lg md:text-xl">
            Your voice can earn you full access.
          </p>
          <p>
            We run contests and reward suggestions that help us make the site better. If your idea
            gets used, you get a free membership. Simple as that. The people who help build this
            deserve to use it.
          </p>
          <p>
            Tell me what you see. Tell me what&rsquo;s missing. Tell me what would make this stronger
            for your classroom.
          </p>
          <p className="font-mono text-base text-white/65 mt-6">
            — Midnite
            <br />
            <span className="text-[#2F8A92]">Founder, Midnite Productions</span>
          </p>
        </div>
        <div className="mt-8">
          <button
            onClick={() => navigate('/contact')}
            data-testid="demo-for-teachers-contact-btn"
            className="inline-flex items-center justify-center gap-2 font-hud text-xs md:text-sm tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-6 py-3 hover:bg-[#D4A84A]/85"
          >
            <BookOpen size={14} /> SHARE YOUR FEEDBACK <ArrowRight size={14} />
          </button>
        </div>
      </section>

      {/* Inline source registry — replaces the removed "Credits" sidebar
          pane so the methodology-essential citations stay visible to
          judges. Apr 29 2026. No background — text-only on the page. */}
      {sourceRegistryEntries.length > 0 && (
        <div className="mt-14 pt-8 border-t border-[#D4A84A]/15" data-testid="demo-inline-sources">
          <div className="font-hud text-[10px] tracking-[0.4em] uppercase text-[#D4A84A] mb-3">
            Sources
          </div>
          <ul className="space-y-2 text-xs md:text-sm">
            {sourceRegistryEntries.map(([id, src]) => (
              <li key={id} className="leading-relaxed">
                {src.url ? (
                  <a
                    href={src.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="cite"
                    title={src.label || src.title || id}
                  >
                    {id}
                  </a>
                ) : (
                  <span className="text-[#D4A84A] font-mono">[{id}]</span>
                )}{' '}
                <span className="text-white/85" style={{ fontFamily: 'Georgia, serif' }}>{src.title || src.publisher || id}</span>
                {src.authors && <span className="text-white/55"> — {src.authors}</span>}
                {src.label && !src.authors && <span className="text-white/55"> · {src.label}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </article>
  );

  return (
    <div className="min-h-screen text-white" style={{ background: '#0A0A0A' }} data-testid="demo-chain-page">
      <ParagraphFollowCursor testId="demo-follow-cursor" />
      <PinholeTransition
        active={false}
        onComplete={handlePinholeComplete}
        imageUrl={pinholeImage}
      />
      {/* PinholeTransition force-disabled Apr 30 2026 per Lead — the
          iris-out animation was visually buggy on production. The element
          is preserved as a no-op so the file structure / refs / tests
          remain intact, but `active` is hard-pinned to false so the
          transition never plays. Will revisit post-launch.
          GlobeSpinIntro removed entirely Apr 30 2026 per Lead — DO NOT
          re-add. The spin overlay strobed on every audio tick. */}
      {/* Top bar — mirrors the HUDLayout immersive bar: a centered episode
           title flanked by a "BACK TO TIME MACHINE" exit on the left. Apr 27
           2026 brief: "At the top were tabs should be centered should say
           back to time machine." */}
      <div className="sticky top-0 z-50 border-b border-[#D4A84A]/15 backdrop-blur-md" style={{ background: 'rgba(10,10,10,0.92)' }}>
        <div className="max-w-6xl mx-auto px-3 md:px-6 py-2.5 flex items-center gap-3">
          <button
            onClick={() => navigate('/world')}
            data-testid="demo-back-to-time-machine"
            className="flex items-center gap-1.5 font-hud text-[10px] md:text-[11px] tracking-widest text-white/85 hover:text-[#D4A84A] transition-colors whitespace-nowrap"
          >
            <ArrowLeft size={13} /> BACK TO WORLD HISTORY
          </button>
          <span
            className="flex-1 text-center font-historical text-[#D4A84A] text-sm md:text-base font-light tracking-tight truncate px-3"
            data-testid="demo-episode-title"
          >
            {chain.title || chain.subject || 'Episode'}
          </span>
          <button
            onClick={() => navigate('/signup')}
            data-testid="demo-signup-btn"
            className="font-hud text-[10px] md:text-xs tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-3 py-1.5 hover:bg-[#D4A84A]/85 whitespace-nowrap hidden md:inline-block"
          >
            BEGIN YOUR JOURNEY
          </button>
        </div>
      </div>

      <EpisodeFrame
        pictureSlot={pictureSlot}
        scriptSlot={scriptSlot}
        testId="demo-chain-episode-frame"
      />
    </div>
  );
}
